import React, { useState, useMemo } from "react";
import { View, StyleSheet, Pressable, Text } from "react-native";
import type { PressableStateCallbackType } from "react-native";
import { Chat, BuildingOffice, GearSix, ListChecks, ArrowSquareOut } from "@phosphor-icons/react";
import { GraduationCap } from "lucide-react";
import { MotiView, AnimatePresence } from "moti";
import AxioCard from "../../axio-ds/Card/AxioCard";
import ThreadList from "../ThreadList/ThreadList";
import { colors, radii, spacing, typographyScale } from "../../../styles/design-system";
import { ArrowLeft, Home, ChevronRight } from "lucide-react";
import Tooltip from "../../common/tooltip/Tooltip";

export interface WorkspaceThreadItem {
  id: string;
  title: string;
  metaLabel?: string;
  isActive: boolean;
  isMainThread?: boolean;
  pinned?: boolean;
}

// this will probably be an enum defined by the schema,
// so we will be able to import it from generated graphql types
export type WorkspaceType =
  | "general"
  | "course"
  | "support"
  | "todo"
  | "settings"
  | "learning_activities"
  | "home"
  | "external"
  | "backToCourses";

// in the actual implementation, instead of passing id, title, type, lastActivity, threadCount
// as separate props, we should pass the actual entity and destructure it here
export interface WorkspaceCardProps {
  id?: string;
  title: string;
  type: WorkspaceType;
  unreadCount?: number;
  isActive?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  isSidebarCollapsed?: boolean;
  threads?: WorkspaceThreadItem[];
  isAccordionExpanded?: boolean;
  onThreadSelect?: (threadId: string) => void;
  onThreadPin?: (threadId: string) => void;
  onThreadRename?: (threadId: string, newName: string) => void;
  onThreadDelete?: (threadId: string) => void;
  onNewThread?: (workspaceId: string) => void;
  threadTotalCount?: number;
  hasMoreThreads?: boolean;
  isThreadLoading?: boolean;
  onThreadLoadMore?: (workspaceId: string) => void;
  activeThreadId?: string | null;
}

const iconSize = 18;

const getIconForType = (type: WorkspaceType, color: string) => {
  if (type === "course") {
    return <GraduationCap size={iconSize} color={color} />;
  }
  if (type === "support") {
    return <BuildingOffice size={iconSize} color={color} />;
  }
  if (type === "todo") {
    return <ListChecks size={iconSize} color={color} />;
  }
  if (type === "settings") {
    return <GearSix size={iconSize} color={color} />;
  }
  if (type === "learning_activities") {
    return <GraduationCap size={iconSize} color={color} />;
  }
  if (type === "home") {
    return <Home size={iconSize} color={color} />;
  }
  if (type === "external") {
    return <ArrowSquareOut size={iconSize} color={color} />;
  }
  if (type === "backToCourses") {
    return <ArrowLeft size={iconSize} color={color} />;
  }
  return <Chat size={iconSize} color={color} />;
};

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
  id,
  title,
  type,
  unreadCount,
  isActive = false,
  disabled = false,
  onPress,
  isSidebarCollapsed = false,
  threads = [],
  isAccordionExpanded = false,
  onThreadSelect,
  onThreadPin,
  onThreadRename,
  onThreadDelete,
  onNewThread,
  threadTotalCount,
  hasMoreThreads,
  isThreadLoading,
  onThreadLoadMore,
  activeThreadId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasThreads = threads.length > 0 && !isSidebarCollapsed;

  const handlePress = () => {
    if (!disabled && onPress) {
      onPress();
    }
  };

  // Custom colors for learning_activities type
  const isLearningActivities = type === "learning_activities";
  const iconBackgroundColor = isLearningActivities ? "#E9D5FF" : isActive ? colors.brand[100] : colors.grey[100];
  const iconColor = isLearningActivities ? "#7E22CE" : isActive ? colors.brand[700] : colors.grey[500];
  const cardBackgroundColor = isLearningActivities && isActive ? "#FAF5FF" : undefined;

  const cardContent = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityHint={type === "external" ? `Open ${title}` : `Open ${title} workspace`}
      disabled={disabled}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}>
      {(state: PressableStateCallbackType) => (
        <AxioCard
          variant={isSidebarCollapsed ? "workspace-collapsed" : "workspace"}
          isActive={isActive}
          disabled={disabled}
          hovered={isHovered && !state.pressed}
          pressed={state.pressed}
          style={[
            { borderRadius: radii.lg },
            cardBackgroundColor ? { backgroundColor: cardBackgroundColor } : undefined,
          ]}>
          <View style={styles.leftSection}>
            <View style={[styles.iconContainer, { backgroundColor: iconBackgroundColor }]}>
              {getIconForType(type, iconColor)}
            </View>
            {!isSidebarCollapsed && (
              <View style={styles.textContainer}>
                <Text
                  style={[
                    styles.titleText,
                    isActive
                      ? isLearningActivities
                        ? styles.titleTextActiveLearning
                        : styles.titleTextActive
                      : styles.titleTextInactive,
                  ]}
                  numberOfLines={1}
                  accessibilityLabel={title}
                  role="heading">
                  {title}
                </Text>
              </View>
            )}
          </View>

          {!isSidebarCollapsed && (
            <View style={styles.rightSection}>
              {!isActive && typeof unreadCount === "number" && unreadCount > 0 ? (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
                </View>
              ) : null}
              {hasThreads && (
                <MotiView
                  animate={{ rotate: isAccordionExpanded ? "90deg" : "0deg" }}
                  // @ts-expect-error - MotiView transition props have complex typing
                  transition={{ type: "timing", duration: 300 }}
                  style={styles.chevron}>
                  <ChevronRight size={14} color={colors.grey[400]} />
                </MotiView>
              )}
            </View>
          )}
        </AxioCard>
      )}
    </Pressable>
  );

  const threadListHeight = useMemo(
    () => Math.min(threads.length, 5) * 44 + 44 + (threadTotalCount && threadTotalCount > 5 ? 40 : 0),
    [threads.length, threadTotalCount]
  );

  const threadListAnimateStyle = useMemo(() => ({ height: threadListHeight, opacity: 1 }), [threadListHeight]);

  const threadList = hasThreads ? (
    <AnimatePresence>
      {isAccordionExpanded && (
        <MotiView
          key="thread-list"
          from={{ height: 0, opacity: 0 }}
          animate={threadListAnimateStyle}
          exit={{ height: 0, opacity: 0 }}
          // @ts-expect-error - MotiView transition props have complex typing
          transition={{
            type: "timing",
            duration: 300,
            height: {
              type: "timing",
              duration: 300,
            },
            opacity: {
              type: "timing",
              duration: 200,
              delay: 50,
            },
          }}
          style={[styles.threadListWrapper, styles.threadListWrapperOpen]}>
          <ThreadList
            isExpanded={isAccordionExpanded}
            workspaceId={id}
            threads={threads}
            onThreadSelect={onThreadSelect}
            onNewThread={onNewThread}
            onThreadPin={onThreadPin}
            onThreadRename={onThreadRename}
            onThreadDelete={onThreadDelete}
            totalCount={threadTotalCount}
            hasMore={hasMoreThreads}
            isLoadingMore={isThreadLoading}
            onLoadMore={onThreadLoadMore && id ? () => onThreadLoadMore(id) : undefined}
            activeThreadId={activeThreadId}
          />
        </MotiView>
      )}
    </AnimatePresence>
  ) : null;

  const content = hasThreads ? (
    <View style={[styles.accordionWrapper, isAccordionExpanded && styles.accordionWrapperOpen]}>
      {cardContent}
      {threadList}
    </View>
  ) : (
    cardContent
  );

  if (!isSidebarCollapsed) {
    return content;
  }

  return (
    <Tooltip content={title} placement="right" horizontalOffset={5} showArrow={false}>
      {content}
    </Tooltip>
  );
};

const styles = StyleSheet.create({
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: spacing.xs,
    overflow: "hidden",
    gap: spacing.xs,
  },
  chevron: {
    flexShrink: 0,
  },
  accordionWrapper: {
    gap: 4,
    position: "relative",
    overflow: "visible",
  },
  accordionWrapperOpen: {
    zIndex: 30,
  },
  threadListWrapper: {
    position: "relative",
    overflow: "hidden",
  },
  threadListWrapperOpen: {
    zIndex: 20,
  },
  iconContainer: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.md,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.grey[100],
  },
  textContainer: {
    flex: 1,
    flexDirection: "column",
    marginLeft: spacing.xs,
    minWidth: 0,
    overflow: "hidden",
  },
  titleText: {
    fontSize: typographyScale.textSm.fontSize,
    fontFamily: "Inter",
    fontWeight: "500",
  },
  titleTextActive: {
    color: colors.brand[700],
  },
  titleTextActiveLearning: {
    color: "#7E22CE",
  },
  titleTextInactive: {
    color: colors.grey[700],
  },
  unreadBadge: {
    minWidth: spacing.lg,
    height: spacing.lg,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.full,
    backgroundColor: colors.brand[500],
    alignItems: "center",
    justifyContent: "center",
  },
  unreadBadgeText: {
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: "600",
    color: colors.white,
  },
});
export default WorkspaceCard;
